import React, { useEffect, useState } from 'react';
import { View, ScrollView, ActivityIndicator } from 'react-native';

import Modal from './shared/Modal';
import Text from './shared/Text';
import { InfoIcon } from './common/Info';
import warriorInfoModalStore from './common/WarriorInfoModal/store';

import { fetchLeaders, Leader } from '../lib/leaderboardCache';
import appStore from '../stores/app';
import { t } from '../utils/appUtils';
import { BASE_COLOR, MARGIN_5 } from './styleConstants';

type Props = { onHide: () => void };

const Cell: React.FC<{ flex: number; bold?: boolean; children?: any }> = ({
  flex,
  bold,
  children,
}) => (
  <View style={{ flex, flexDirection: 'row', alignItems: 'center', paddingHorizontal: 4 }}>
    {typeof children === 'string' || typeof children === 'number' ? (
      <Text bold={bold}>{String(children)}</Text>
    ) : (
      children
    )}
  </View>
);

const Row: React.FC<{ header?: boolean; children?: any }> = ({ header, children }) => (
  <View
    style={{
      flexDirection: 'row',
      paddingVertical: 4,
      borderBottomWidth: header ? 1 : 0,
      borderBottomColor: '#8f6b50',
      marginBottom: header ? MARGIN_5 : 0,
    }}
  >
    {children}
  </View>
);

export default function LeaderboardModal({ onHide }: Props) {
  const [leaders, setLeaders] = useState<Leader[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchLeaders(appStore.initData);
        if (!cancelled) setLeaders(data);
      } catch (e: any) {
        if (cancelled) return;
        appStore.showMessage({ type: 'error', text: e?.message || String(e) });
        onHide();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal title={t('leaderboard.title')} onHide={onHide} fullScreen warriorListener>
      <View style={{ marginTop: 40, paddingLeft: 40, paddingBottom: 40, paddingTop: 20, flex: 1 }}>
        {loading || !leaders ? (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <ActivityIndicator color={BASE_COLOR} />
          </View>
        ) : (
          <ScrollView>
            <Row header>
              <Cell flex={0.2} bold>
                {t('leaderboard.columns.rank')}
              </Cell>
              <Cell flex={2} bold>
                {t('leaderboard.columns.login')}
              </Cell>
              <Cell flex={1.3} bold>
                {t('leaderboard.columns.experience')}
              </Cell>
              <Cell flex={1.2} bold>
                {t('leaderboard.columns.klass')}
              </Cell>
              <Cell flex={0.8} bold>
                {t('leaderboard.columns.wins')}
              </Cell>
              <Cell flex={0.8} bold>
                {t('leaderboard.columns.losses')}
              </Cell>
            </Row>
            {leaders.map((l) => (
              <Row key={l.id}>
                <Cell flex={0.2}>{l.rank}</Cell>
                <Cell flex={2}>
                  <Text bold>{`${l.login} [${l.level}]`}</Text>
                  <InfoIcon
                    style={{ marginLeft: 5 }}
                    onPress={() => warriorInfoModalStore.toggle(l.id, true)}
                  />
                </Cell>
                <Cell flex={1.3}>{l.experience}</Cell>
                <Cell flex={1.2}>{t(`leaderboard.klasses.${l.klass}`)}</Cell>
                <Cell flex={0.8}>{l.wins}</Cell>
                <Cell flex={0.8}>{l.losses}</Cell>
              </Row>
            ))}
          </ScrollView>
        )}
      </View>
    </Modal>
  );
}
